import type {
  OnyxMapCustomRoute,
  OnyxMapFitContentFilters,
  OnyxMapMarkerData,
  OnyxMapRouteOptions,
  OnyxMapRoutePoint,
  OnyxMapTooltip,
  OnyxMapVehicleOptions,
  OnyxRoute,
} from '.';
import type { OnyxOptional } from '../../../interfaces';
import type { OnyxCoordinates } from '../../address-input/interfaces';
import type { OnyxMapMarkerType, OnyxMapVehicleStatus } from '../enums';
import { OnyxMapEventType } from '../enums';

export type OnyxMapEvent =
  | OnyxMapEvent.AddUpdateVehicle
  | OnyxMapEvent.RemoveVehicle
  | OnyxMapEvent.SelectVehicles
  | OnyxMapEvent.AddUpdateRoutePoint
  | OnyxMapEvent.RemoveRoutePoint
  | OnyxMapEvent.AddUpdateRoute
  | OnyxMapEvent.AddUpdateCustomRoute
  | OnyxMapEvent.RemoveRoute
  | OnyxMapEvent.AddUpdateMarker
  | OnyxMapEvent.RemoveMarker
  | OnyxMapEvent.FitContent;

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace OnyxMapEvent {
  export class AddUpdateVehicle {
    public readonly TYPE = OnyxMapEventType.ADD_UPDATE_VEHICLE;
    public readonly id: string;
    public readonly status: OnyxMapVehicleStatus;
    public readonly coordinates: OnyxOptional<OnyxCoordinates>;
    public readonly azimuth: OnyxOptional<number>;
    public readonly options?: OnyxMapVehicleOptions;

    constructor(
      private readonly data: {
        id: string;
        status: OnyxMapVehicleStatus;
        coordinates: OnyxOptional<OnyxCoordinates>;
        azimuth: OnyxOptional<number>;
        options?: OnyxMapVehicleOptions;
      },
    ) {
      ({
        id: this.id,
        status: this.status,
        coordinates: this.coordinates,
        azimuth: this.azimuth,
        options: this.options,
      } = data);
    }
  }

  export class RemoveVehicle {
    public readonly TYPE = OnyxMapEventType.REMOVE_VEHICLE;
    public readonly id: string;

    constructor(private readonly data: { id: string }) {
      ({ id: this.id } = data);
    }
  }

  export class SelectVehicles {
    public readonly TYPE = OnyxMapEventType.SELECT_VEHICLES;
    public readonly ids: string[];

    constructor(
      private readonly data: {
        ids: string[];
      },
    ) {
      ({ ids: this.ids } = data);
    }
  }

  export class AddUpdateRoute {
    public readonly TYPE = OnyxMapEventType.ADD_UPDATE_ROUTE;
    public readonly id: number;
    public readonly route: OnyxRoute;
    public readonly points: OnyxMapRoutePoint[];
    public readonly options?: OnyxMapRouteOptions;
    public readonly callback: (index: number) => void;

    constructor(
      private readonly data: {
        id: number;
        route: OnyxRoute;
        points: OnyxMapRoutePoint[];
        options?: OnyxMapRouteOptions;
        callback: (index: number) => void;
      },
    ) {
      ({
        id: this.id,
        route: this.route,
        points: this.points,
        callback: this.callback,
        options: this.options,
      } = data);
    }
  }

  export class AddUpdateCustomRoute {
    public readonly TYPE = OnyxMapEventType.ADD_UPDATE_CUSTOM_ROUTE;
    public readonly id: number;
    public readonly route: OnyxMapCustomRoute;
    public readonly options?: OnyxMapRouteOptions;
    public readonly callback: () => void;

    constructor(
      private readonly data: {
        id: number;
        route: OnyxMapCustomRoute;
        options?: OnyxMapRouteOptions;
        callback: () => void;
      },
    ) {
      ({
        id: this.id,
        route: this.route,
        options: this.options,
        callback: this.callback,
      } = data);
    }
  }

  export class RemoveRoute {
    public readonly TYPE = OnyxMapEventType.REMOVE_ROUTE;
    public readonly id: number;

    constructor(private readonly data: { id: number }) {
      ({ id: this.id } = data);
    }
  }

  export class AddUpdateRoutePoint {
    public readonly TYPE = OnyxMapEventType.ADD_UPDATE_ROUTE_POINT;
    public readonly point: OnyxMapRoutePoint;
    public readonly callback: () => void;

    constructor(
      private readonly data: {
        point: OnyxMapRoutePoint;
        callback: () => void;
      },
    ) {
      ({ point: this.point, callback: this.callback } = data);
    }
  }

  export class RemoveRoutePoint {
    public readonly TYPE = OnyxMapEventType.REMOVE_ROUTE_POINT;
    public readonly id: string;

    constructor(private readonly data: { id: string }) {
      ({ id: this.id } = data);
    }
  }

  export class AddUpdateMarker {
    public readonly TYPE = OnyxMapEventType.ADD_UPDATE_MARKER;
    public readonly id: string;
    public readonly type: OnyxMapMarkerType;
    public readonly coordinates: OnyxCoordinates;
    public readonly callback?: () => void;
    public readonly tooltip?: OnyxMapTooltip<OnyxMapMarkerData>;
    public readonly active?: boolean;

    constructor(
      private readonly data: {
        id: string;
        type: OnyxMapMarkerType;
        coordinates: OnyxCoordinates;
        callback?: () => void;
        tooltip?: OnyxMapTooltip<OnyxMapMarkerData>;
        active?: boolean;
      },
    ) {
      ({
        id: this.id,
        type: this.type,
        coordinates: this.coordinates,
        callback: this.callback,
        tooltip: this.tooltip,
        active: this.active,
      } = data);
    }
  }

  export class RemoveMarker {
    public readonly TYPE = OnyxMapEventType.REMOVE_MARKER;
    public readonly id: string;

    constructor(private readonly data: { id: string }) {
      ({ id: this.id } = data);
    }
  }

  export class FitContent {
    public readonly TYPE = OnyxMapEventType.FIT_CONTENT;

    constructor(public readonly filters?: OnyxMapFitContentFilters) {}
  }
}
